import React, { useState, useEffect, useCallback } from 'react';
import { getPermisos, deletePermiso } from '../services/permisoService';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, Edit, Trash2, ArrowLeft, RefreshCw, AlertCircle, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ShieldCheck } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function PermisosList() {
    const [permisos, setPermisos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [moduleFilter, setModuleFilter] = useState('TODOS');

    const navigate = useNavigate();
    const { token } = useAuth();

    const cargarPermisos = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getPermisos();
            setPermisos(data);
            setError(null);
        } catch (error) {
            console.error('Error al cargar permisos:', error);
            setError('No se pudieron cargar los permisos.');
            toast.error('Error al cargar los permisos');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        cargarPermisos();
    }, [cargarPermisos]);

    const handleEliminar = async (id) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar este permiso?')) {
            try {
                await deletePermiso(id);
                toast.success('Permiso eliminado exitosamente');
                cargarPermisos();
            } catch (error) {
                console.error('Error al eliminar:', error);
                toast.error('No se pudo eliminar el permiso');
            }
        }
    };

    // Filter and Search Logic
    const filteredPermisos = permisos.filter(p => {
        const matchesSearch = p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (p.descripcion && p.descripcion.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesModule = moduleFilter === 'TODOS' || p.modulo === moduleFilter;
        return matchesSearch && matchesModule;
    });

    const uniqueModules = ['TODOS', ...new Set(permisos.map(p => p.modulo).filter(Boolean))];

    return (
        <div className="max-w-6xl mx-auto bg-white shadow-sm border border-gray-200 mb-6">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="bg-slate-700 p-3 flex justify-between items-center text-white">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/seguridad')}
                        className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                        title="Volver a Seguridad"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-blue-400" />
                        <h1 className="text-lg font-semibold tracking-tight uppercase">Mantenimiento: Gestión de Permisos</h1>
                    </div>
                </div>

                <Link
                    to="/seguridad/permisos/nuevo"
                    className="flex items-center gap-1 transition-all rounded-sm text-[0.85rem] font-medium px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                >
                    <PlusCircle className="w-4 h-4" />
                    NUEVO PERMISO
                </Link>
            </div>

            {/* Controls */}
            <div className="p-3 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4 bg-gray-50">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <label className="text-xs font-bold text-gray-600 uppercase">Filtro Módulo:</label>
                        <select
                            value={moduleFilter}
                            onChange={(e) => setModuleFilter(e.target.value)}
                            className="border border-gray-300 p-1 bg-white focus:ring-1 focus:ring-blue-500 outline-none text-[0.875rem] rounded-sm"
                        >
                            {uniqueModules.map(m => (
                                <option key={m} value={m}>{m}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="relative">
                    <input
                        type="text"
                        placeholder="Buscar por nombre o desc..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="border border-gray-300 pl-8 pr-3 py-1.5 w-64 focus:ring-1 focus:ring-blue-500 outline-none text-[0.875rem] rounded-sm"
                    />
                    <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                </div>
            </div>

            {/* Content Table Area */}
            <div className="overflow-x-auto relative">
                {error && (
                    <div className="m-3 p-3 bg-red-50 border border-red-200 text-red-700 flex items-center rounded-sm text-sm">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        {error}
                    </div>
                )}

                <div className="min-w-full">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-[#f8fafc] border-b-2 border-slate-200 text-gray-700 text-xs uppercase tracking-wider">
                                <th className="px-4 py-3 border-r border-gray-200 w-16 text-center">ID</th>
                                <th className="px-4 py-3 border-r border-gray-200">Clave Permiso</th>
                                <th className="px-4 py-3 border-r border-gray-200">Módulo</th>
                                <th className="px-4 py-3 border-r border-gray-200">Descripción</th>
                                <th className="px-4 py-3 text-center w-40">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm text-gray-600">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-4 py-8 text-center">
                                        <RefreshCw className="w-6 h-6 text-blue-500 animate-spin mx-auto" />
                                    </td>
                                </tr>
                            ) : filteredPermisos.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500 italic">
                                        No se encontraron permisos registrados.
                                    </td>
                                </tr>
                            ) : (
                                filteredPermisos.map((p) => (
                                    <tr key={p.id} className="border-b border-gray-200 hover:bg-[#f1f5f9] transition-colors">
                                        <td className="px-4 py-2 border-r border-gray-200 text-center font-mono text-xs text-gray-500">
                                            {p.id}
                                        </td>
                                        <td className="px-4 py-2 border-r border-gray-200 font-bold text-gray-800">
                                            {p.nombre}
                                        </td>
                                        <td className="px-4 py-2 border-r border-gray-200">
                                            <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-[10px] font-bold border border-slate-200 rounded-sm">
                                                {p.modulo || 'SIN MÓDULO'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 border-r border-gray-200 text-gray-500 text-xs italic">
                                            {p.descripcion || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-center flex justify-center items-center gap-2">
                                            <Link
                                                to={`/seguridad/permisos/${p.id}/editar`}
                                                className="text-blue-600 hover:text-blue-800 font-semibold text-xs transition-colors"
                                                title="Editar"
                                            >
                                                EDITAR
                                            </Link>
                                            <span className="text-gray-300">|</span>
                                            <button
                                                onClick={() => handleEliminar(p.id)}
                                                className="text-red-600 hover:text-red-800 font-semibold text-xs transition-colors"
                                                title="Eliminar"
                                            >
                                                ELIMINAR
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Footer */}
            <div className="p-3 bg-gray-50 border-t border-gray-200 text-[0.75rem] text-gray-400 italic">
                Nota: Los permisos son claves de sistema (ej: socios.read). No modifique las claves si no está seguro del impacto.
            </div>
        </div>
    );
}
